import { useCallback, useContext, useEffect, useId, useRef } from 'react';
import { HotkeyContext, type HotkeyBinding } from './HotkeyContext';

/**
 * Регистрирует набор горячих клавиш, пока компонент смонтирован (и `enabled !== false`).
 * `bindings` можно передавать новым массивом на каждый рендер — актуальные замыкания хранятся
 * в ref, сама запись в стеке не пересоздаётся. Возвращает `touch()` — вызвать при получении
 * окном фокуса (клик), чтобы поднять его биндинги на вершину стека (несколько окон без маски
 * можно переключать в любом порядке, а не только по монтированию — см. `HotkeyProvider`).
 */
export function useHotkeyScope(bindings: HotkeyBinding[], enabled = true): { touch: () => void } {
  const registry = useContext(HotkeyContext);
  const id = useId();
  const bindingsRef = useRef(bindings);

  useEffect(() => {
    bindingsRef.current = bindings;
  }, [bindings]);

  useEffect(() => {
    if (!registry || !enabled) return;
    registry.push({ id, bindingsRef });
    return () => registry.remove(id);
  }, [registry, enabled, id]);

  const touch = useCallback(() => registry?.touch(id), [registry, id]);
  return { touch };
}
